// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import {
  Anchor,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import type { DiagnosticReport, Patient, ServiceRequest } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { TableAnchor } from '../../../components/layout/TableAnchor';
import type { HsatOrderRow } from '../../../hooks/usePatientHsatOrders';
import { useRole } from '../../../hooks/useRole';
import { hasPermission, ROLES } from '../../../utils/permissions';

const PAGE_SIZE = 10;

function formatDate(value: string | undefined): string {
  if (!value) {
    return '—';
  }
  return dayjs(value).format('MM/DD/YYYY');
}

function getCptCode(request: ServiceRequest): string {
  return (
    request.code?.coding?.find((c) => c.system === 'http://www.ama-assn.org/go/cpt')?.code ??
    request.code?.coding?.[0]?.code ??
    '—'
  );
}

function getSerial(request: ServiceRequest): string {
  return request.identifier?.[0]?.value ?? '—';
}

function getStatus(request: ServiceRequest): string {
  const status = request.status;
  switch (status) {
    case 'active':
      return 'Ordered';
    case 'completed':
      return 'Delivered';
    case 'revoked':
      return 'Revoked';
    case 'draft':
      return 'Draft';
    default:
      return status ?? '—';
  }
}

function getSuspectedDiagnoses(request: ServiceRequest): string {
  const codes = (request.reasonCode ?? []).map((c) => c.coding?.[0]?.code).filter((c): c is string => Boolean(c));
  return codes.length ? codes.join(', ') : '—';
}

function getExpires(request: ServiceRequest): string {
  return formatDate(request.occurrencePeriod?.end);
}

function getTestDate(request: ServiceRequest): string {
  return formatDate(request.occurrencePeriod?.start ?? request.occurrenceDateTime);
}

function getNoteText(request: ServiceRequest): string {
  return (request.note ?? [])
    .map((n) => n.text)
    .filter(Boolean)
    .join('\n\n');
}

function isReportViewable(status: DiagnosticReport['status']): boolean {
  return status === 'final' || status === 'amended' || status === 'corrected' || status === 'appended';
}

export interface HsatSectionProps {
  patient: Patient;
  orders: HsatOrderRow[];
  loading: boolean;
}

export function HsatSection({ patient, orders, loading }: HsatSectionProps): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { role, isAdmin } = useRole();
  const canOrder = hasPermission({ role, permission: 'place-hsat-order', isAdmin });
  const isNursePractitioner = role === ROLES.NURSE_PRACTITIONER;

  const openPdf = useCallback(
    async (report: DiagnosticReport) => {
      const rawUrl = report.presentedForm?.[0]?.url;
      if (!rawUrl) {
        return;
      }
      const href = rawUrl.startsWith('Binary/') ? URL.createObjectURL(await medplum.download(rawUrl)) : rawUrl;
      window.open(href, '_blank', 'noopener,noreferrer');
    },
    [medplum]
  );

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [noteTarget, setNoteTarget] = useState<ServiceRequest | undefined>();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return orders;
    }
    return orders.filter((o) => {
      const hay = [
        getCptCode(o.request),
        getSerial(o.request),
        getStatus(o.request),
        o.orderedBy?.name?.[0] ? formatHumanName(o.orderedBy.name[0]) : '',
        getSuspectedDiagnoses(o.request),
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [orders, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const paginated = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  return (
    <Paper withBorder radius="md" shadow="sm" p="xl">
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={2}>HSAT</Title>
        <Group gap="sm">
          <TextInput
            placeholder="Search…"
            value={query}
            onChange={(e) => {
              setQuery(e.currentTarget.value);
              setPage(1);
            }}
            leftSection={<IconSearch size={14} />}
            size="sm"
            w={240}
            aria-label="Search HSAT orders"
          />
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => navigate(`/OrderHSAT/${patient.id}`)}
            disabled={!canOrder}
            title={!canOrder ? 'You do not have permission to order HSATs' : undefined}
          >
            Order
          </Button>
        </Group>
      </Group>

      {loading ? (
        <Stack align="center" py="md">
          <Loader size="sm" />
        </Stack>
      ) : (
        <Box style={{ overflowX: 'auto' }}>
          <Table highlightOnHover withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Test</Table.Th>
                <Table.Th>Code(s)</Table.Th>
                <Table.Th>Serial</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Ordered by</Table.Th>
                <Table.Th>Order Date</Table.Th>
                <Table.Th>Test Date</Table.Th>
                <Table.Th>Suspected Diagnoses</Table.Th>
                <Table.Th>Expires</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {paginated.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={10}>
                    <Text ta="center" c="dimmed" py="md">
                      No HSAT orders on file.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                paginated.map((row) => (
                  <Table.Tr key={row.request.id}>
                    <Table.Td>{row.request.code?.text ?? row.request.code?.coding?.[0]?.display ?? 'HSAT'}</Table.Td>
                    <Table.Td>{getCptCode(row.request)}</Table.Td>
                    <Table.Td>{getSerial(row.request)}</Table.Td>
                    <Table.Td>{getStatus(row.request)}</Table.Td>
                    <Table.Td>
                      {row.orderedBy?.id ? (
                        <TableAnchor
                          component="button"
                          type="button"
                          size="sm"
                          onClick={() => navigate(`/Practitioner/${row.orderedBy?.id}`)}
                        >
                          {row.orderedBy.name?.[0] ? formatHumanName(row.orderedBy.name[0]) : 'Provider'}
                        </TableAnchor>
                      ) : (
                        '—'
                      )}
                    </Table.Td>
                    <Table.Td>{formatDate(row.request.authoredOn)}</Table.Td>
                    <Table.Td>{getTestDate(row.request)}</Table.Td>
                    <Table.Td>{getSuspectedDiagnoses(row.request)}</Table.Td>
                    <Table.Td>{getExpires(row.request)}</Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {row.diagnosticReport?.presentedForm?.[0]?.url &&
                          (!isNursePractitioner || isReportViewable(row.diagnosticReport.status)) && (
                            <TableAnchor
                              component="button"
                              type="button"
                              size="sm"
                              onClick={() => {
                                if (row.diagnosticReport) {
                                  openPdf(row.diagnosticReport).catch(console.error);
                                }
                              }}
                            >
                              View Results
                            </TableAnchor>
                          )}
                        <TableAnchor
                          component="button"
                          type="button"
                          size="sm"
                          onClick={() => setNoteTarget(row.request)}
                          disabled={!row.request.note?.length}
                        >
                          Note
                        </TableAnchor>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {filtered.length > PAGE_SIZE && (
        <Group justify="space-between" mt="sm">
          <Text size="sm" c="dimmed">{`${filtered.length} of ${orders.length}`}</Text>
          <Pagination value={pageSafe} onChange={setPage} total={totalPages} size="sm" />
        </Group>
      )}

      <Modal opened={Boolean(noteTarget)} onClose={() => setNoteTarget(undefined)} title="HSAT Order Notes" centered>
        <Text style={{ whiteSpace: 'pre-wrap' }}>{noteTarget ? getNoteText(noteTarget) : ''}</Text>
      </Modal>
    </Paper>
  );
}
